import { CourierTravelModeString } from '../../../types/bundleTypes';
import dayjs from 'dayjs';
import {
  CourierDeliveryZoneCountsByDate,
  CourierInvoice,
} from '../../../types/reportTypes';
import {
  ShiftCourierJson,
  ShiftCourierWithDateJson,
  ShiftStatusType,
} from '../../../const/ShiftConst';
import { ReqEnum } from '../../../const/requestStatus';
import { PerDayInvoice, ShiftForDate } from './arrayManipulation2';
import { calcCostPerDayInvoice } from './calcCostPerDayInvoice';
import { returnNullIfObjectEmpty } from './returnNullIfObjectEmpty';

export function prodCostTableForCourierWithShiftData(
  courierInvoiceForSelectedCourier: CourierInvoice,
  travelMode: string = CourierTravelModeString.CAR.toString(),
  courierListBookedFetched: ShiftCourierWithDateJson[],
  fetchStatusShift: string,
  useOutsideOsloCompensationModel: boolean
): PerDayInvoice[] {
  returnNullIfObjectEmpty(courierInvoiceForSelectedCourier);

  if (fetchStatusShift !== ReqEnum.SUCCESS) {
    return null;
  }

  const shiftsForThisCourier = getShiftsForThisCourier(
    courierListBookedFetched,
    courierInvoiceForSelectedCourier
  );

  const emptyDeliveryArrayWithDates: CourierDeliveryZoneCountsByDate[] =
    getEmptyCourierDeliveryZoneCountsByDateToAdd(
      courierInvoiceForSelectedCourier,
      shiftsForThisCourier
    );

  const deliveryArray: CourierDeliveryZoneCountsByDate[] = getFirstArray(
    courierInvoiceForSelectedCourier
  );

  const sortedCourierDeliveryZoneCountsByDate: CourierDeliveryZoneCountsByDate[] =
    getSortedCourierDeliveryZoneCountsByDate(
      deliveryArray,
      emptyDeliveryArrayWithDates
    );

  return getPerDayInvoiceList(
    sortedCourierDeliveryZoneCountsByDate,
    shiftsForThisCourier,
    travelMode,
    courierInvoiceForSelectedCourier.courierAvgRatingForDateRange,
    useOutsideOsloCompensationModel
  );
}

function getSortedCourierDeliveryZoneCountsByDate(
  firstArray: CourierDeliveryZoneCountsByDate[],
  newArrayToMap: CourierDeliveryZoneCountsByDate[]
): CourierDeliveryZoneCountsByDate[] {
  return [...firstArray, ...newArrayToMap].sort(function (
    a: CourierDeliveryZoneCountsByDate,
    b: CourierDeliveryZoneCountsByDate
  ) {
    const key1 = a.date;
    const key2 = b.date;

    if (key1 < key2) {
      return -1;
    } else if (key1 === key2) {
      return 0;
    } else {
      return 1;
    }
  });
}

function getPerDayInvoiceList(
  newCountsArraytoMap: CourierDeliveryZoneCountsByDate[],
  shiftsForThisCourier: ShiftCourierWithDateJson[],
  travelMode: string,
  courierAvgRatingForDateRange: number,
  useOutsideOsloCompensationModel: boolean
): PerDayInvoice[] {
  return newCountsArraytoMap.map(
    (
      courierDeliveryZoneCountsByDate: CourierDeliveryZoneCountsByDate,
      index: number
    ) => {
      const shiftByDate: ShiftCourierJson = ShiftForDate(
        shiftsForThisCourier,
        courierDeliveryZoneCountsByDate
      );

      return calcCostPerDayInvoice(
        courierDeliveryZoneCountsByDate,
        travelMode,
        shiftByDate,
        courierAvgRatingForDateRange,
        useOutsideOsloCompensationModel
      );
    }
  );
}

function getFirstArray(
  courierInvoiceForSelectedCourier: CourierInvoice
): CourierDeliveryZoneCountsByDate[] {
  if (courierInvoiceForSelectedCourier.counts === undefined) {
    return [];
  } else {
    return courierInvoiceForSelectedCourier.counts;
  }
}

function getEmptyCourierDeliveryZoneCountsByDateToAdd(
  courierInvoiceForSelectedCourier: CourierInvoice,
  shiftsForThisCourier: ShiftCourierWithDateJson[]
): CourierDeliveryZoneCountsByDate[] {
  const arrayOfDatesFromInvoiceData: string[] = getArrayOfDatesFromInvoiceData(
    courierInvoiceForSelectedCourier
  );

  const shiftsThatDontHaveADeliveryDate = getShiftsThatDontHaveADeliveryDate(
    shiftsForThisCourier,
    arrayOfDatesFromInvoiceData
  );

  const newArrayToMap: CourierDeliveryZoneCountsByDate[] =
    getEmptyCourierDeliveryZoneCountsByDate(shiftsThatDontHaveADeliveryDate);

  return newArrayToMap;
}

function getEmptyCourierDeliveryZoneCountsByDate(
  shiftsThatDontHaveADeliveryDate: dayjs.Dayjs[]
): CourierDeliveryZoneCountsByDate[] {
  return shiftsThatDontHaveADeliveryDate.map(obj => {
    const EmptyCourierDeliveryZoneCountsByDate: CourierDeliveryZoneCountsByDate =
      {
        date: dayjs(obj).format('YYYY-MM-DD'),
        totalCount: 0,
        deliveriesOnDate: [],
      };
    return EmptyCourierDeliveryZoneCountsByDate;
  });
}

function getShiftsThatDontHaveADeliveryDate(
  shiftsForThisCourier: ShiftCourierWithDateJson[],
  arrayOfDatesFromInvoiceData: string[]
) {
  return shiftsForThisCourier
    .filter(
      obj =>
        obj.date &&
        obj.courierDayBooked.filter(
          courierObj =>
            courierObj.status === ShiftStatusType.BOOKED &&
            courierObj.extraWork.length
        ).length
    )
    .map(obj => obj.date)
    .filter(
      date =>
        arrayOfDatesFromInvoiceData.indexOf(
          dayjs(date).format('YYYY-MM-DD')
        ) === -1
    );
}

function getArrayOfDatesFromInvoiceData(
  courierInvoiceForSelectedCourier: CourierInvoice
): string[] {
  return courierInvoiceForSelectedCourier.counts &&
    courierInvoiceForSelectedCourier.counts.length
    ? courierInvoiceForSelectedCourier.counts.map(
        courierDeliveryZoneCountsByDate =>
          dayjs(courierDeliveryZoneCountsByDate.date).format('YYYY-MM-DD')
      )
    : [];
}

function getShiftsForThisCourier(
  courierListBookedFetched: ShiftCourierWithDateJson[],
  courierInvoiceForSelectedCourier: CourierInvoice
): ShiftCourierWithDateJson[] {
  return courierListBookedFetched.map((obj: ShiftCourierWithDateJson) => {
    const filteredObj: ShiftCourierWithDateJson = {
      date: obj.date,
      courierDayBooked: obj.courierDayBooked
        ? obj.courierDayBooked.filter(
            (shift: ShiftCourierJson) =>
              shift.courierId === courierInvoiceForSelectedCourier.courierId // is undefined here , ned to fix
          )
        : [],
    };
    return filteredObj;
  });
}
